// ============================================================================
// HOOK - useSimulator.js
// ============================================================================
// ./hooks/useSimulator.js

import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { CourseDomains } from '../types/courseDomains';
import { GlobalContext } from '../context/provider';
import simulatorAPI from '../api/simulatorAPI'

const useSimulator = () => {
  const { 
    simulatorState,
    simulatorDispatch
  } = useContext(GlobalContext);

  // ── IIoT engine state ─────────────────────────────────────────────────────
  // Only active when currentSimulator.domain === 'IIoT'.
  // IoT behaviour is completely unchanged.
  const [engineRunning,  setEngineRunning]  = useState(false);
  const [messagesRouted, setMessagesRouted] = useState(0);
  const [mqttConnected,  setMqttConnected]  = useState(false);
  const [engineLoading,  setEngineLoading]  = useState(false);
  const [engineError,    setEngineError]    = useState(null);
  const [liveData,       setLiveData]       = useState(null);
  const [engineSensors,  setEngineSensors]  = useState([]);
  const [isGenerating,   setIsGenerating]   = useState(false);
  const [generateError,  setGenerateError]  = useState(null);

  // Refs — stable across renders, readable inside setInterval closures
  const statusPollRef    = useRef(null);
  const liveDataPollRef  = useRef(null);
  // Frontend cap safety net — backend also enforces cap independently
  // Reset to 0 on each startEngine call
  const snapshotCountRef = useRef(0);
  const [snapshotCount, setSnapshotCount] = useState(0);
  
  // ============================================================================
  // SIMULATOR MANAGEMENT
  // ============================================================================

  const getAllSimulators = useCallback(async (params = {}) => {
    simulatorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await simulatorAPI.getAllSimulators(params);
      if (response.success) {
        simulatorDispatch({ type: 'SET_SIMULATORS', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get simulators' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const getSimulatorBySessionId = useCallback(async (sessionId) => {
    simulatorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await simulatorAPI.getSimulatorBySessionId(sessionId);
      if (response.success) {
        simulatorDispatch({ type: 'SET_CURRENT_SIMULATOR', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get simulator' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const getSimulatorById = useCallback(async (id) => {
    simulatorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await simulatorAPI.getSimulatorById(id);
      if (response.success) {
        simulatorDispatch({ type: 'SET_CURRENT_SIMULATOR', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get simulator' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const createSimulator = useCallback(async (data) => {
    simulatorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await simulatorAPI.createSimulator(data);
      if (response.success) {
        simulatorDispatch({ type: 'ADD_SIMULATOR', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to create simulator' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const updateSimulator = useCallback(async (id, data) => {
    simulatorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await simulatorAPI.updateSimulator(id, data);
      if (response.success) {
        simulatorDispatch({ type: 'UPDATE_SIMULATOR', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to update simulator' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const deleteSimulator = useCallback(async (id) => {
    simulatorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await simulatorAPI.deleteSimulator(id);
      if (response.success) {
        simulatorDispatch({ type: 'DELETE_SIMULATOR', payload: id });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to delete simulator' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  // ============================================================================
  // SIMULATOR CONTROL
  // ============================================================================
  const stopEngineLiveDataPolling = useCallback(() => {
    if (liveDataPollRef.current) {
      clearInterval(liveDataPollRef.current);
      liveDataPollRef.current = null;
    }
  }, []);

  const stopEngineStatusPolling = useCallback(() => {
    if (statusPollRef.current) {
      clearInterval(statusPollRef.current);
      statusPollRef.current = null;
    }
  }, []);

  const startEngineStatusPolling = useCallback((sessionId) => {
    if (statusPollRef.current) return;
    const interval = CourseDomains.IIoT.polling.baseInterval; // 3000ms

    statusPollRef.current = setInterval(async () => {
      try {
        const response = await simulatorAPI.getEngineStatus(sessionId);
        if (response?.data) {
          const { running, messagesRouted: mr, mqttConnected: mc } = response.data;
          setEngineRunning(running);
          setMessagesRouted(mr ?? 0);
          setMqttConnected(mc ?? false);

          // Backend reported engine stopped (cap reached or manual stop)
          if (!running) {
            clearInterval(statusPollRef.current);
            statusPollRef.current = null;
            // stopEngineLiveDataPolling();
            // setLiveData(null);
          }
        }
      } catch {
        // silent — polling is best-effort
      }
    }, interval);
  }, [stopEngineLiveDataPolling]);

  const startEngineLiveDataPolling = useCallback((sessionId, simulatorId) => {
    if (liveDataPollRef.current) return;
    const interval = CourseDomains.IIoT.polling.baseInterval; // 3000ms
    const cap      = CourseDomains.IIoT.polling.cap;           // 20

    liveDataPollRef.current = setInterval(async () => {
      try {
        const response = await simulatorAPI.getEngineLiveData(sessionId);

        if (response?.success && response?.data) {
          setLiveData(response.data);

          // Save snapshot — backend enforces cap too, this is a frontend safety net
          if (simulatorId && response.data) {
            const snapshot = {
              timestamp:      new Date(),
              sensorText:     response.data.sensorText     || {},
              actuatorStates: response.data.actuatorStates || {},
              plcToGateway:   response.data.plcToGateway   || { protocol: '', formatted: '' },
              gatewayToAzure: response.data.gatewayToAzure || { protocol: '', formatted: '' },
              azureDecoded:   response.data.azureDecoded   || {},
              nodeLastData:   response.data.nodeLastData   || {},
            };

            snapshotCountRef.current += 1;
            setSnapshotCount(snapshotCountRef.current);
            if (snapshotCountRef.current <= cap) {
              simulatorAPI.addSimulationSnapshot(simulatorId, snapshot).catch(() => {});
            }
            // Frontend stops polling at cap — backend may have already stopped engine
            if (snapshotCountRef.current >= cap) {
              stopEngineLiveDataPolling();
              stopEngineStatusPolling();
              setEngineRunning(false);
              setLiveData(null);
            }
          }
        }

        if (response?.sensors) {
          setEngineSensors(response.sensors);
        }
      } catch {
        // silent
      }
    }, interval);
  }, [stopEngineLiveDataPolling, stopEngineStatusPolling]);

  // Read IIoT identity from context — set when simulator loads
  const currentDomain    = simulatorState.currentSimulator?.domain;
  const currentSessionId = simulatorState.currentSimulator?.sessionId;
  const currentSimId     = simulatorState.currentSimulator?._id;

  useEffect(() => {
    // Only activate for IIoT simulators
    if (currentDomain !== 'IIoT' || !currentSessionId) return;

    // On mount, check if engine is already running (e.g. page refresh mid-run)
    simulatorAPI.getEngineStatus(currentSessionId)
      .then(response => {
        if (response?.data?.running) {
          setEngineRunning(true);
          setMessagesRouted(response.data.messagesRouted ?? 0);
          setMqttConnected(response.data.mqttConnected  ?? false);
          startEngineStatusPolling(currentSessionId);
          startEngineLiveDataPolling(currentSessionId, currentSimId);
        }
      })
      .catch(() => {});

    // Cleanup on unmount — stop all polling intervals
    return () => {
      stopEngineStatusPolling();
      stopEngineLiveDataPolling();
    };
  }, [
    currentDomain,
    currentSessionId,
    currentSimId,
    startEngineStatusPolling,
    startEngineLiveDataPolling,
    stopEngineStatusPolling,
    stopEngineLiveDataPolling,
  ]);

  const startSimulator = useCallback(async (id) => {
    try {
      const response = await simulatorAPI.startSimulator(id);
      if (response.success) {
        simulatorDispatch({ type: 'START_SIMULATOR', payload: response.data });
      } else {
        // ✅ Don't dispatch error for validation failures - handled by caller
        if (!response.message?.includes('validation failed')) {
          simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to start simulator' });
        }
      }
      return response;
    } catch (error) {
      // ✅ Don't dispatch error for validation failures - handled by caller
      if (!error.message?.includes('validation failed')) {
        simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      }
      throw error;
    }
  }, [simulatorDispatch]);

  const validateSimulator = useCallback(async (id) => {
    try {
      const response = await simulatorAPI.validateSimulator(id);
      if (response.success) {
        simulatorDispatch({ type: 'SET_VALIDATION', payload: response.validation });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const stopSimulator = useCallback(async (id) => {
    try {
      const response = await simulatorAPI.stopSimulator(id);
      if (response.success) {
        simulatorDispatch({ type: 'STOP_SIMULATOR', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to stop simulator' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const pauseSimulator = useCallback(async (id) => {
    try {
      const response = await simulatorAPI.pauseSimulator(id);
      if (response.success) {
        simulatorDispatch({ type: 'PAUSE_SIMULATOR', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to pause simulator' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const resumeSimulator = useCallback(async (id) => {
    try {
      const response = await simulatorAPI.resumeSimulator(id);
      if (response.success) {
        simulatorDispatch({ type: 'START_SIMULATOR', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to resume simulator' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const resetSimulator = useCallback(async (id) => {
    try {
      const response = await simulatorAPI.resetSimulator(id);
      if (response.success) {
        simulatorDispatch({ type: 'RESET_SIMULATOR', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to reset simulator' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const getSimulatorStatus = useCallback(async (id) => {
    try {
      const response = await simulatorAPI.getSimulatorStatus(id);
      if (response.success) {
        simulatorDispatch({ type: 'UPDATE_STATUS', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get status' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  // ✅ ADD THIS NEW HOOK FUNCTION
  const getActuatorStates = useCallback(async (id) => {
    try {
      const response = await simulatorAPI.getActuatorStates(id);
      if (response.success) {
        simulatorDispatch({ type: 'UPDATE_ACTUATOR_STATES', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get actuator states' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  // ============================================================================
  // CODE UPLOAD
  // ============================================================================

  const uploadCode = useCallback(async (id, code, language = 'Arduino') => {
    simulatorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await simulatorAPI.uploadCode(id, code, language);
      if (response.success) {
        simulatorDispatch({ type: 'UPDATE_SIMULATOR', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to upload code' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  // ============================================================================
  // SENSOR OPERATIONS
  // ============================================================================

  const attachSensor = useCallback(async (id, sensorData) => {
    try {
      const response = await simulatorAPI.attachSensor(id, sensorData);
      if (response.success) {
        simulatorDispatch({ type: 'ATTACH_SENSOR', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to attach sensor' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const detachSensor = useCallback(async (id, sensorId) => {
    try {
      const response = await simulatorAPI.detachSensor(id, sensorId);
      if (response.success) {
        simulatorDispatch({ type: 'DETACH_SENSOR', payload: { simulatorId: id, sensorId } });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to detach sensor' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const readSensor = useCallback(async (id, sensorId) => {
    try {
      const response = await simulatorAPI.readSensor(id, sensorId);
      if (response.success) {
        simulatorDispatch({ 
          type: 'UPDATE_SENSOR_VALUE', 
          payload: { sensorId, value: response.data.value, timestamp: response.data.timestamp } 
        });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to read sensor' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  // ============================================================================
  // GPIO OPERATIONS
  // ============================================================================

  const writeGPIO = useCallback(async (id, pin, value) => {
    try {
      const response = await simulatorAPI.writeGPIO(id, pin, value);
      if (response.success) {
        simulatorDispatch({ type: 'UPDATE_GPIO_STATE', payload: { pin, value } });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to write GPIO' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const readGPIO = useCallback(async (id, pin) => {
    try {
      const response = await simulatorAPI.readGPIO(id, pin);
      if (response.success) {
        simulatorDispatch({ type: 'UPDATE_GPIO_STATE', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to read GPIO' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const configureGPIO = useCallback(async (id, pin, mode, type) => {
    try {
      const response = await simulatorAPI.configureGPIO(id, pin, mode, type);
      if (response.success) {
        simulatorDispatch({ type: 'UPDATE_GPIO_CONFIG', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to configure GPIO' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  // ============================================================================
  // WIFI OPERATIONS
  // ============================================================================

  const connectWiFi = useCallback(async (id, ssid, password) => {
    try {
      const response = await simulatorAPI.connectWiFi(id, ssid, password);
      if (response.success) {
        simulatorDispatch({ type: 'CONNECT_WIFI', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to connect WiFi' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const disconnectWiFi = useCallback(async (id) => {
    try {
      const response = await simulatorAPI.disconnectWiFi(id);
      if (response.success) {
        simulatorDispatch({ type: 'DISCONNECT_WIFI', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to disconnect WiFi' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  // ============================================================================
  // SERIAL OUTPUT
  // ============================================================================

  const getSerialOutput = useCallback(async (id, limit = 100) => {
    try {
      const response = await simulatorAPI.getSerialOutput(id, limit);
      if (response.success) {
        simulatorDispatch({ type: 'SET_SERIAL_OUTPUT', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get serial output' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const sendSerialInput = useCallback(async (id, input) => {
    try {
      const response = await simulatorAPI.sendSerialInput(id, input);  // ✅ input!
      if (response.success) {
        // Don't need to dispatch anything, just return success
        return response;
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to send serial input' });
        return response;
      }
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const getRunningSimulators = useCallback(async () => {
    simulatorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await simulatorAPI.getRunningSimulators();
      if (response.success) {
        simulatorDispatch({ type: 'SET_RUNNING_SIMULATORS', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get running simulators' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const getActiveCount = useCallback(async () => {
    try {
      const response = await simulatorAPI.getActiveCount();
      if (response.success) {
        simulatorDispatch({ type: 'SET_ACTIVE_COUNT', payload: response.data.activeCount });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get active count' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const updateSensorConfig = useCallback(async (simulatorId, sensorId, configData) => {
    simulatorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await simulatorAPI.updateSensorConfig(simulatorId, sensorId, configData);
      if (response.success) {
        simulatorDispatch({ type: 'UPDATE_SIMULATOR', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  // ============================================================================
  // UTILITY
  // ============================================================================

  const clearError = useCallback(() => {
    simulatorDispatch({ type: 'CLEAR_ERROR' });
  }, [simulatorDispatch]);

  const clearCurrentSimulator = useCallback(() => {
    simulatorDispatch({ type: 'CLEAR_CURRENT_SIMULATOR' });
  }, [simulatorDispatch]);

  const clearData = useCallback(() => {
    simulatorDispatch({ type: 'CLEAR_SIMULATOR_DATA' });
  }, [simulatorDispatch]);

  // ============================================================================
  // CODE GENERATION
  // ============================================================================

  const mergeComponentTemplates = useCallback(async (componentIds, boardType = 'ESP32', simulatorConfigs = null) => {
    try {
      const response = await simulatorAPI.mergeComponentTemplates(componentIds, boardType, simulatorConfigs);
      if (!response.success) {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to merge templates' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const generateNotebookCells = useCallback(async (componentIds, circuitComponents) => {
    try {
      const response = await simulatorAPI.generateNotebookCells(componentIds, circuitComponents);
      if (!response.success) {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to generate notebook cells' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  // Saves notebookCells array to simulator record via existing updateSimulator endpoint
  const saveNotebookCells = useCallback(async (simulatorId, notebookCells) => {
    try {
      const response = await simulatorAPI.saveNotebookCells(simulatorId, notebookCells);
      if (response.success) {
        simulatorDispatch({ type: 'UPDATE_SIMULATOR', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to save notebook cells' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  // useSimulator.js — add gradingData parameter:
  const saveGradingPayload = useCallback(async (id, duration, gradingData = {}) => {
    try {
      const response = await simulatorAPI.saveGradingPayload(id, duration, gradingData);
      if (response.success) {
        simulatorDispatch({ type: 'UPDATE_SIMULATOR', payload: response.data });
      } else {
        simulatorDispatch({
          type: 'SET_ERROR',
          payload: response.error || 'Failed to save grading payload'
        });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const submitGrades = useCallback(async (id) => {
    try {
      const response = await simulatorAPI.submitGrades(id);
      if (response.success) {
        simulatorDispatch({ type: 'UPDATE_SIMULATOR', payload: response.data });
      } else {
        simulatorDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to submit grades' });
      }
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const addSimulationSnapshot = useCallback(async (id, snapshot) => {
    try {
      const response = await simulatorAPI.addSimulationSnapshot(id, snapshot);
      return response;
    } catch (error) {
      console.error('Failed to save snapshot:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // ============================================================================
  // IIOT ENGINE CONTROL
  // Calls the unified /api/simulator/engine/* endpoints via simulatorAPI.
  // Polling state (liveData, engineRunning etc.) is added in Step 3.
  // sessionId is the primary key for all engine operations.
  // ============================================================================
  const startEngine = useCallback(async (sessionId) => {
    setEngineLoading(true);
    setEngineError(null);
    snapshotCountRef.current = 0; // reset cap counter for new run
    setSnapshotCount(0);
    try {
      const response = await simulatorAPI.startEngine(sessionId);
      if (response?.success) {
        setEngineRunning(true);
        startEngineStatusPolling(sessionId);
        // currentSimId is read from context — already set before this call
        startEngineLiveDataPolling(sessionId, currentSimId);
      } else {
        simulatorDispatch({
          type: 'SET_ERROR',
          payload: response.error || 'Failed to start engine'
        });
      }
      return response;
    } catch (error) {
      setEngineError(error.message);
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      setEngineLoading(false);
    }
  }, [currentSimId, simulatorDispatch, startEngineStatusPolling, startEngineLiveDataPolling]);

  const stopEngine = useCallback(async (sessionId) => {
    setEngineLoading(true);
    setEngineError(null);
    try {
      const response = await simulatorAPI.stopEngine(sessionId);
      if (response?.success) {
        setEngineRunning(false);
        setMessagesRouted(0);
        setMqttConnected(false);
        setLiveData(null);
        stopEngineStatusPolling();
        stopEngineLiveDataPolling();
      } else {
        simulatorDispatch({
          type: 'SET_ERROR',
          payload: response.error || 'Failed to stop engine'
        });
      }
      return response;
    } catch (error) {
      setEngineError(error.message);
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      setEngineLoading(false);
    }
  }, [simulatorDispatch, stopEngineStatusPolling, stopEngineLiveDataPolling]);

  const getEngineStatus = useCallback(async (sessionId) => {
    try {
      const response = await simulatorAPI.getEngineStatus(sessionId);
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const getEngineLiveData = useCallback(async (sessionId) => {
    try {
      const response = await simulatorAPI.getEngineLiveData(sessionId);
      return response;
    } catch (error) {
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [simulatorDispatch]);

  const generateNodeRedFunction = useCallback(async (params) => {
    setIsGenerating(true);
    setGenerateError(null);
    try {
      const response = await simulatorAPI.generateNodeRedFunction(params);
      if (!response?.success) {
        const msg = response?.error || 'Failed to generate Node-RED function';
        setGenerateError(msg);
        simulatorDispatch({ type: 'SET_ERROR', payload: msg });
      }
      return response;
    } catch (error) {
      setGenerateError(error.message);
      simulatorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      setIsGenerating(false);
    }
  }, [simulatorDispatch]);

  const handleEngineRun = useCallback((sessionId) => {
    return engineRunning ? stopEngine(sessionId) : startEngine(sessionId);
  }, [engineRunning, startEngine, stopEngine]);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // State
    simulators: simulatorState.simulators,
    currentSimulator: simulatorState.currentSimulator,
    studentSimulators: simulatorState.studentSimulators,
    labSimulators: simulatorState.labSimulators,
    courseSimulators: simulatorState.courseSimulators,
    runningSimulators: simulatorState.runningSimulators,
    activeCount: simulatorState.activeCount,
    serialOutput: simulatorState.serialOutput,
    sensorReadings: simulatorState.sensorReadings,
    gpioStates: simulatorState.gpioStates,
    isLoading: simulatorState.isLoading,
    error: simulatorState.error,
    snapshotCount,

    // Simulator Management
    getAllSimulators,
    getSimulatorBySessionId,
    getSimulatorById,
    createSimulator,
    updateSimulator,
    deleteSimulator,

    // Simulator Control
    startSimulator,
    validateSimulator,
    stopSimulator,
    pauseSimulator,
    resumeSimulator,
    resetSimulator,
    getSimulatorStatus,
    getActuatorStates,
    
    // Code Upload
    uploadCode,

    // Sensor Operations
    attachSensor,
    detachSensor,
    readSensor,

    // GPIO Operations
    writeGPIO,
    readGPIO,
    configureGPIO,

    // WiFi Operations
    connectWiFi,
    disconnectWiFi,

    // Serial Output
    getSerialOutput,
    sendSerialInput,
    getRunningSimulators,
    getActiveCount,
    updateSensorConfig,
    
    // Code Generation
    mergeComponentTemplates,
    generateNotebookCells,
    saveNotebookCells,
    saveGradingPayload,
    submitGrades,
    addSimulationSnapshot,

    // IIoT Engine state
    engineRunning,
    messagesRouted,
    mqttConnected,
    engineLoading,
    engineError,
    liveData,
    engineSensors,
    isGenerating,
    generateError,
    setGenerateError,

    // IIoT Engine control
    startEngine,
    stopEngine,
    handleEngineRun,
    getEngineStatus,
    getEngineLiveData,
    generateNodeRedFunction,

    // Utility
    clearError,
    clearCurrentSimulator,
    clearData
  };
};

export default useSimulator;